import { z } from 'zod'
import {
  CorrectRecipientRequest,
  CorrectRecipientResponse,
  ExtendExpiryRequest,
  ExtendExpiryResponse,
  ResendInvitationRequest,
  ResendInvitationResponse,
  VoidEnvelopeRequest,
} from './api.js'
import { McpActionRef, McpCreditEffect, McpReviewOperation } from './mcp-review.js'

/**
 * The managed delivery path and the operational families — SM5 (B7.3, B7.5, B11.1, B18.1;
 * MCP-AC-047..049, AT-015/016, MCP-AC-128, DEC-06).
 *
 * Four families, one shape: **prepare freezes the action and states its credit effect, a person
 * approves it in the trusted surface, and the commit re-validates the live state before it runs.**
 * The plan's own words for this slice are "prepare/commit pairs on the same approval model", so
 * this module reuses SM3's spine rather than inventing a second one: the review, the approval
 * token, the decision route, the status poll and the commit idempotency are the same code, and the
 * only thing that differs is what the frozen action *is* (see `McpReviewOperation` in
 * `mcp-review.ts`, which the same summary carries).
 *
 * Three decisions shape the shapes here, and each is why a rule is structural rather than a
 * promise:
 *
 * 1. **The request is the application's own request.** Each prepare request is the *same* schema the
 *    sender's own REST route validates (`CorrectRecipientRequest`, `ResendInvitationRequest`,
 *    `ExtendExpiryRequest`, `VoidEnvelopeRequest`) plus the identifiers the REST path carries in its
 *    URL. There is therefore no second interpretation of "which fields a correction may change",
 *    "which revision a resend quotes" or "how long an extension may ask for" — a divergence would
 *    be a schema difference, which a reviewer sees.
 * 2. **The outcome is the application's own outcome.** The commit's `operation` block embeds
 *    `CorrectRecipientResponse` / `ResendInvitationResponse` / `ExtendExpiryResponse` verbatim, so
 *    the same operation through MCP and through the app answers with the same fields. `VOID` is the
 *    one exception: the app's void route answers `204` with no body, so the MCP outcome states the
 *    facts the batch actually produced (what was revoked, what was skipped) instead of inventing a
 *    body the app does not have.
 * 3. **No tool claims a delivery.** `McpDeliveryReport` is literals: the managed branch reports the
 *    invitation as *queued*, the delivery state as `UNKNOWN`, and names the ledger that reconciles
 *    it. A provider's ambiguous outcome stays ambiguous (MCP-AC-048) — the service never upgrades
 *    "queued" into "sent", and the manual branch's link is the sender's own capability, never a
 *    receipt.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()

/**
 * The scope the *managed* branch of an operation additionally requires. A managed operation makes
 * SignBento dispatch an invitation through its own provider, which is a delivery act and not an
 * agreement edit; the manual branch hands the sender the capability they already hold and needs
 * nothing beyond `agreements.write`.
 */
export const MCP_DELIVERY_SCOPE = 'delivery.write' as const

/* ------------------------------ prepare requests ---------------------------- */

/**
 * The four prepare requests. Each is the application's own request shape extended with the ids its
 * REST path carries in the URL, so `envelope_id`/`recipient_id` are the only new fields — and there
 * is no field for a document, a path, a base64 blob, a field value or an approval in any of them.
 */
export const McpCorrectionPrepareRequest = CorrectRecipientRequest.extend({
  envelope_id: Id,
  recipient_id: Id,
}).strict()
export type McpCorrectionPrepareRequest = z.infer<typeof McpCorrectionPrepareRequest>

export const McpResendPrepareRequest = ResendInvitationRequest.extend({
  envelope_id: Id,
  recipient_id: Id,
}).strict()
export type McpResendPrepareRequest = z.infer<typeof McpResendPrepareRequest>

export const McpVoidPrepareRequest = VoidEnvelopeRequest.extend({
  envelope_id: Id,
  /**
   * The envelope's operational revision the caller read. The app's own void route has no such
   * field (a void is terminal and idempotent by state); the MCP path adds it because a *prepared*
   * void must be able to say which state it was prepared against, and because an approval bound to
   * a revision is the spine's rule (STM-10).
   */
  expected_revision: z.number().int().nonnegative(),
}).strict()
export type McpVoidPrepareRequest = z.infer<typeof McpVoidPrepareRequest>

export const McpExpiryExtensionPrepareRequest = ExtendExpiryRequest.extend({
  envelope_id: Id,
}).strict()
export type McpExpiryExtensionPrepareRequest = z.infer<typeof McpExpiryExtensionPrepareRequest>

/** The one request a commit needs, for every family: the review and the approval token. */
export const McpOperationCommitRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    review_id: Id,
    review_token: z.string().min(1).max(512).optional(),
  })
  .strict()
export type McpOperationCommitRequest = z.infer<typeof McpOperationCommitRequest>

/* --------------------------------- delivery --------------------------------- */

/**
 * What the service can honestly say about a managed invitation: it queued it. The provider's
 * outcome is not observable here — the worker records what a provider told it in
 * `invitation_deliveries`, and until then the state is `UNKNOWN`. The literals exist so no reader
 * can mistake this report for a receipt (MCP-AC-048, OP-021).
 */
export const McpDeliveryReport = z
  .object({
    /** `QUEUED` for a managed invitation; `NOT_APPLICABLE` for a manual reveal or a non-delivery op. */
    dispatch: z.enum(['QUEUED', 'NOT_APPLICABLE']),
    /** Where the outcome would be observable, when it is observable at all. */
    state: z.enum(['UNKNOWN', 'NOT_APPLICABLE']),
    /** The ledger that reconciles it. Named, so "unknown" is a pointer and not a shrug. */
    reconciled_by: z.enum(['invitation_deliveries', 'sender_reveal']),
    /** Stated in the payload so no caller can claim the service said it sent something. */
    delivery_claimed: z.literal(false),
    link_open_claimed: z.literal(false),
  })
  .strict()
export type McpDeliveryReport = z.infer<typeof McpDeliveryReport>

/** The queued invitation, as the app's own outcome shape describes it (never a link when managed). */
export const McpManagedDispatch = z
  .object({
    /** The logical outbox event the invitation was committed under — the dedup key, not a job id. */
    logical_event_id: Id,
    /** The slot the invitation occupies for this participant: the resend budget's own unit. */
    slot_index: z.number().int().positive(),
    kind: z.enum(['invitation', 'resend', 'correction']),
  })
  .strict()
export type McpManagedDispatch = z.infer<typeof McpManagedDispatch>

/* --------------------------------- outcomes --------------------------------- */

/** The void outcome: the facts the batch produced, since the app's void route answers no body. */
export const McpVoidOutcome = z
  .object({
    envelope_id: Id,
    state: z.literal('VOIDED'),
    voided_at: IsoDateTime,
    /**
     * Literals rather than counts, and deliberately: the batch's own statements revoke *every*
     * capability of the agreement that is still outstanding and skip *every* queued invitation, so
     * "all of them" is a fact the batch guarantees. A count read before the batch could be wrong,
     * and a count read after it would not be part of the atomic answer.
     */
    capabilities_revoked: z.literal(true),
    queued_invitations_skipped: z.literal(true),
    reason: z.string().max(500).nullable(),
  })
  .strict()
export type McpVoidOutcome = z.infer<typeof McpVoidOutcome>

/**
 * The performed operation. Each family embeds the application's own response contract, so the same
 * operation answers the same fields on both surfaces; the managed branch additionally names the
 * logical dispatch it committed.
 */
export const McpOperationOutcome = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('CORRECTION'), correction: CorrectRecipientResponse }).strict(),
  z.object({ kind: z.literal('RESEND'), resend: ResendInvitationResponse }).strict(),
  z.object({ kind: z.literal('VOID'), void: McpVoidOutcome }).strict(),
  z.object({ kind: z.literal('EXPIRY_EXTENSION'), extension: ExtendExpiryResponse }).strict(),
])
export type McpOperationOutcome = z.infer<typeof McpOperationOutcome>

/**
 * The commit result — the stored result a replay returns verbatim. There is no `capability_links`
 * field at all: the manual branch's link lives inside the app's own `InvitationOutcome`, which
 * cannot carry one on the managed branch, so "no link when managed" is a shape rather than a check.
 */
export const McpOperationCommitResult = z
  .object({
    review_id: Id,
    state: z.literal('COMMITTED'),
    committed_at: IsoDateTime,
    action: McpActionRef,
    operation: McpOperationOutcome,
    /** The managed branch's committed dispatch, or `null` when nothing was queued. */
    dispatch: McpManagedDispatch.nullable(),
    delivery: McpDeliveryReport,
    /**
     * The credit effect the person approved, restated. An operational edit never costs another
     * delivery credit — a reminder is not a second delivery (AT-015, MCP-AC-025) — so this is
     * `none`/`0`, and the balance is the live one at commit.
     */
    credit_effect: McpCreditEffect,
    /** True when this answer came from the stored result rather than a new consumption. */
    idempotent_replay: z.boolean(),
  })
  .strict()
export type McpOperationCommitResult = z.infer<typeof McpOperationCommitResult>

/** Re-exported so a consumer of the operations contract needs one import for the frozen action. */
export { McpReviewOperation }
